// Renders a live, scaled slide preview into a host element — the mechanism
// behind deck-card and rail thumbnails. Reuses the vanilla renderer + the
// slide-stage runtime (loaded app-wide), so a thumbnail is the real slide
// scaled down, never a screenshot. Idempotent: it marks the host hydrated and
// skips re-work unless the slide signature changes.

import { renderSlide, type RegionStyle } from './render-slides';
import type { Slide } from './slide-model';

interface SlideStageGlobal {
  fitAll(root: Element): void;
  // `noVideo` opts a hydrate out of mounting a real YouTube player over the
  // poster — slide-stage.js already honours it (overview/notes hosts use it).
  // Thumbnails MUST pass it so a grid of video-bg slides shows static posters
  // rather than spinning up dozens of cross-origin <iframe> players.
  hydrate(slide: Element, opts: { animate: boolean; noVideo?: boolean }): void;
}

/** Mount (or refresh) a scaled preview of `slide` inside `host`. `sig` is a
 *  cheap change key so repeated calls are no-ops until the rendered content
 *  changes. Inline-edit `overrides` and `regionStyles` (rich-text heading size +
 *  alignment) are applied so a thumbnail shows the same text/background/format as
 *  the live slide. */
export function mountSlideThumb(
  host: HTMLElement,
  slide: Slide,
  sig: string,
  overrides: Record<string, string> = {},
  regionStyles: Record<string, RegionStyle> = {},
): void {
  if (host.getAttribute('data-thumb-sig') === sig) return;
  host.setAttribute('data-thumb-sig', sig);
  // Strip the kenburns motion class from the static markup: a motion bg slide
  // otherwise renders `.art.kenburns`, whose CSS animation runs forever in every
  // grid cell. A thumbnail is a still preview — no perpetual pan/zoom. The class
  // is always emitted as " kenburns" (leading space) by the renderer, so this
  // removes it cleanly without leaving a double space.
  const markup = renderSlide(slide, overrides, undefined, regionStyles)
    .replace(/ kenburns(?=["' ])/g, '');
  host.innerHTML =
    '<div class="slide-scaler fit-width"><div class="slide-box"><div class="slide-mount">' +
    markup +
    '</div></div></div>';
  const stage = (window as unknown as { SlideStage?: SlideStageGlobal }).SlideStage;
  if (!stage) return;
  stage.fitAll(host);
  // noVideo: thumbnails show the static YouTube poster, never a live player.
  host.querySelectorAll('.slide').forEach((s) => stage.hydrate(s, { animate: false, noVideo: true }));
  // Re-fit one frame after mount: the synchronous fit above can run before the
  // host has settled to its final size (e.g. while a modal/panel is still
  // opening), which would leave the slide scaled-and-centred with a gap. The
  // ResizeObserver only re-fires if the box later changes, so this guarantees a
  // correct fill even when the size was already final at mount.
  requestAnimationFrame(() => stage.fitAll(host));
}

/** A content signature so a thumbnail refreshes when the slide's appearance
 *  changes (variant / background / image / an inline-edit override / a rich-text
 *  region style), not just when its id changes. Image data URLs are summarised by
 *  length to stay cheap. */
export function contentSig(slide: Slide, overrides?: Record<string, string>, regionStyles?: Record<string, RegionStyle>): string {
  const s = slide as Slide & {
    variant?: string; art?: string; img?: string; layout?: string; theme?: string; font?: string;
    background?: { type?: string; art?: string; img?: string; tint?: number; side?: string };
    panelImg?: string; panelArt?: string;
    items?: { img?: string; art?: string }[];
  };
  const img = s.img ? 'img' + String(s.img).length : '';
  const b = s.background;
  const bg = b ? [b.type || '', b.art || '', b.img ? 'bi' + String(b.img).length : '', b.tint ?? '', b.side || ''].join(',') : '';
  // The side-panel media slot (agenda / kpi / bullets / cta `with-image`). NOT
  // captured by `bg` above, so a generated/changed panel image would otherwise
  // leave the sig — and thus the cached thumbnail — stale until a full reload.
  const panel = s.panelImg ? 'pi' + String(s.panelImg).length : (s.panelArt ? 'pa' + s.panelArt : '');
  // Gallery cells: one img/art per items[] entry. Each cell is a `gal:<i>` slot
  // the studio can fill independently, so the sig must move when any cell does.
  let gal = '';
  if (Array.isArray(s.items)) {
    s.items.forEach((it, i) => {
      if (it && typeof it === 'object') {
        if (it.img) gal += `${i}gi${String(it.img).length}`;
        else if (it.art) gal += `${i}ga${it.art}`;
      }
    });
  }
  let ov = '';
  if (overrides) for (const k of Object.keys(overrides)) ov += k + (overrides[k] || '').length;
  // Stable signature of region styles so a style-only edit (heading size /
  // alignment) refreshes the thumbnail. Sorted so key order can't churn the sig.
  let rs = '';
  if (regionStyles) {
    for (const k of Object.keys(regionStyles).sort()) {
      const r = regionStyles[k];
      if (r) rs += `${k}:${r.h ?? ''}${r.align ?? ''}`;
    }
  }
  // include per-slide theme/font so a self-theme override refreshes the thumbnail
  return [s.variant || '', s.art || '', img, s.layout || '', bg, panel, gal, s.theme || '', s.font || '', ov, rs].join('~');
}

/** Hydrate every `[data-thumb]` host under `root` whose slide is provided by
 *  `lookup(id)`. `overridesFor(id)` optionally supplies inline-edit overrides and
 *  `regionStylesFor(id)` the rich-text heading/align styles, so the thumbnail
 *  matches the edited slide. The cache key is id + content sig, so background /
 *  layout / text / style edits refresh the preview. */
export function mountThumbs(
  root: ParentNode,
  lookup: (id: string) => Slide | undefined,
  overridesFor?: (id: string) => Record<string, string> | undefined,
  regionStylesFor?: (id: string) => Record<string, RegionStyle> | undefined,
): void {
  root.querySelectorAll<HTMLElement>('[data-thumb]').forEach((host) => {
    const id = host.getAttribute('data-thumb') || '';
    const slide = lookup(id);
    if (!slide) return;
    const ov = overridesFor ? overridesFor(id) : undefined;
    const rs = regionStylesFor ? regionStylesFor(id) : undefined;
    mountSlideThumb(host, slide, id + '#' + contentSig(slide, ov, rs), ov || {}, rs || {});
  });
}
